import React, { useState, useEffect, useId } from 'react';
import { MdTune, MdClose } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { inModalLayer, useModalDialog } from 'reusable/useModalDialog';
import { clientCandidatePage, taskCandidatePage } from 'components/Client/Utility/candidates';
import { useRemoteSearch } from 'reusable/RemoteSearch/useRemoteSearch';
import RemoteListFooter from 'reusable/RemoteSearch/RemoteListFooter';
import { isImeComposing } from 'reusable/isImeComposing';

interface FilterProps {
  onTaskSelect: (taskId: string | null, taskName: string) => void;
  onClientSelect: (clientIds: string[], clientNames: string[]) => void;
}

const Filter: React.FC<FilterProps> = ({ onTaskSelect, onClientSelect }) => {
  const { t } = useTranslation('client');
  const [showPopup, setShowPopup] = useState(false);
  const [selectedTask, setSelectedTask] = useState<{
    id: string | null;
    name: string;
  }>({ id: null, name: 'All Tasks' });
  const [selectedClients, setSelectedClients] = useState<
    { id: string; name: string }[]
  >([]);
  // 空は未選択。表示は翻訳した「フィルター」にする。
  const [filterDisplay, setFilterDisplay] = useState('');


  const [tempTask, setTempTask] = useState(selectedTask);
  const [tempClients, setTempClients] = useState<
    { id: string; name: string }[]
  >([]);

  // EV-493。作業・配送先の候補は全件（最大5,000件・50回ずつ）を読まず、絞り込みのダイアログを開いたときに
  // 名前で検索して20件ずつ読む（続きは「もっと見る」）。選んだ作業・配送先は名前ごと持つ（候補に無くても出す）。
  const taskSearch = useRemoteSearch<any>({
    fetchPage: taskCandidatePage,
    getKey: (task) => task.id,
    enabled: showPopup,
  });
  const clientSearch = useRemoteSearch<any>({
    fetchPage: clientCandidatePage,
    getKey: (client) => client.id,
    enabled: showPopup,
  });
  const tasks = taskSearch.items;
  // 以前と同じく、候補を読み終えるまで（失敗の間も）適用させない。
  const loading = taskSearch.status !== 'loaded' || clientSearch.status !== 'loaded';
  const resetSearches = () => {
    taskSearch.setQuery('');
    clientSearch.setQuery('');
  };

  useEffect(() => {
    const formatList = (list: { name: string }[], maxItems: number): string => {
      if (list.length <= maxItems) {
        return list.map((item) => item.name).join(', ');
      }
      return `${list
        .slice(0, maxItems)
        .map((item) => item.name)
        .join(', ')}...`;
    };

    if (selectedTask.id === null && selectedClients.length === 0) {
      setFilterDisplay('');
    } else {
      const taskDisplay =
        selectedTask.id === null ? '' : selectedTask.name;
      const clientsDisplay =
        selectedClients.length === 0 ? '' : formatList(selectedClients, 5);
      setFilterDisplay(
        [taskDisplay, clientsDisplay].filter(Boolean).join(', ')
      );
    }
  }, [selectedTask, selectedClients]);

  const togglePopup = () => {
    setShowPopup(!showPopup);
  };

  // ADM-12。作業・配送先の名前は一意ではない。選択は ID で持ち、名前から ID を引かない
  // （同じ名前の2件目を選んでも、1件目の ID を送っていた）。
  const handleTaskChange = (task: { id: string | null; name: string }) => {
    setTempTask(task);
  };

  const handleClientChange = (client: { id: string; name: string }) => {
    setTempClients((prevClients) =>
      prevClients.some((c) => String(c.id) === String(client.id))
        ? prevClients.filter((c) => String(c.id) !== String(client.id))
        : [...prevClients, { id: client.id, name: client.name }]
    );
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    clientSearch.setQuery(event.target.value);
  };
  // 検索の欄の Enter で、ほかの操作（適用など）をしない。変換の確定（IME）は妨げない。
  const keepEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !isImeComposing(event)) event.preventDefault();
  };

  const deselectAllClients = () => {
    setTempClients([]);
  };

  const filteredClients = clientSearch.items;

  const applyFilters = () => {
    setSelectedTask(tempTask);
    setSelectedClients(tempClients);
    onTaskSelect(tempTask.id, tempTask.name);
    onClientSelect(
      tempClients.map((client) => client.id),
      tempClients.map((client) => client.name)
    );
    togglePopup();
  };

  // 適用済みの絞り込みを外す（絞り込みの表示の右の × ボタン）。親へ「絞り込みなし」を知らせる。
  const clearFilters = () => {
    setSelectedTask({ id: null, name: 'All Tasks' });
    setSelectedClients([]);
    setTempTask({ id: null, name: 'All Tasks' });
    setTempClients([]);
    resetSearches();
    setShowPopup(false);
    onTaskSelect(null, 'All Tasks');
    onClientSelect([], []);
  };

  // ADM-16（2026-10-11 PM 判断）。「閉じる」・Escape は選びかけだけを捨て、適用済みの絞り込みは残す。
  // 以前は clearFilters と同じで、ダイアログを開いて閉じるだけで適用中の絞り込みが外れていた。
  const discardChanges = () => {
    setTempTask(selectedTask);
    setTempClients(selectedClients);
    resetSearches();
    setShowPopup(false);
  };
  // ADM-16。絞り込みのダイアログ。Escape は「閉じる」と同じ（選びかけを捨てる）。
  // RV-125。ダイアログの名前の id は文書の中で一意にする。
  const titleId = useId();
  const dialogRef = useModalDialog(showPopup, discardChanges);

  return (
    <div className="relative flex p-2 justify-center items-center border-r px-4 mr-3">
      <div className="flex flex-row cursor-pointer items-center">
        <button type="button"
          onClick={togglePopup}
          className={`flex items-center px-4 py-2 rounded-l-md ${
            // EV-412。gray-400 は白の上で 2.54。gray-600 で 7.56。
            filterDisplay === ''
              ? 'text-gray-600'
              : 'text-primary/300 bg-primary/50'
          }`}
        >
          {filterDisplay || t('filter.title')}
        </button>
        {filterDisplay === '' ? (
          // 左の「フィルター」と同じ操作。読み上げとキーボードでは左のボタンを使う。
          <button type="button" aria-hidden="true" tabIndex={-1} onClick={togglePopup}>
            <MdTune className="text-gray-600" />
          </button>
        ) : (
          <div className="py-3 px-1 bg-primary/50 rounded-r-md text-primary/300">
            <button
              type="button"
              aria-label={t('ui.clear-filter', { ns: 'common' })}
              onClick={clearFilters}
            >
              <MdClose aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
      {showPopup && inModalLayer(
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 text-black">
          <div
            className="bg-white w-1/2 max-h-4/5 shadow-lg border rounded z-10 overflow-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            ref={dialogRef}
            tabIndex={-1}
          >
            <h6 id={titleId} className="text-xl font-semibold mb-4 px-8 py-4 text-subtitle-1">
              {t('filter.title')}
            </h6>
            <div className="border-t overflow-auto max-h-1/2">
              <h6 className="text-lg font-medium mb-2 px-8 py-2 text-gray-600 text-subtitle-2">
                {t('filter.by_task')}
              </h6>
              <div className="px-8 py-2">
                {/* EV-493。作業名で検索する（サーバーの検索）。 */}
                <input
                  type="text"
                  aria-label={t('filter.task_placeholder')}
                  placeholder={t('filter.task_placeholder')}
                  value={taskSearch.query}
                  onChange={(event) => taskSearch.setQuery(event.target.value)}
                  onKeyDown={keepEnter}
                  className="border w-full rounded-md py-2 mb-4 text-left px-2"
                />
                <div className="grid grid-cols-4 gap-2">
                  <label className="block mb-2">
                    <input
                      className="mr-2"
                      type="radio"
                      name="task"
                      value=""
                      checked={tempTask.id === null}
                      onChange={() => handleTaskChange({ id: null, name: 'All Tasks' })}
                    />
                    {t('filter.all_task')}
                  </label>
                  {tasks.map((task) => (
                    <label key={task.id} className="block mb-2">
                      <input
                        className="mr-2"
                        type="radio"
                        name="task"
                        value={String(task.id)}
                        checked={tempTask.id !== null && String(tempTask.id) === String(task.id)}
                        onChange={() => handleTaskChange({ id: task.id, name: task.name })}
                      />
                      {task.name}
                    </label>
                  ))}
                </div>
                {/* EV-493。読み込み中・空・失敗（既存の文言と再読み込み）・続き（もっと見る）。 */}
                <RemoteListFooter search={taskSearch} />
              </div>
            </div>
            <div className="border-b overflow-auto max-h-1/2">
              <div className="flex flex-row justify-between items-center">
                <h6 className="text-4xl font-medium px-8 py-4">
                  {t('filter.by_client')}
                </h6>
                <button type="button"
                  onClick={deselectAllClients}
                  className="px-8 underline text-primary/200 rounded-md"
                >
                  {t('filter.deselect_all')}
                </button>
              </div>
              <div className="px-8 py-2">
                <input
                  type="text"
                  placeholder={t('filter.client_placeholder')}
                  value={clientSearch.query}
                  onChange={handleSearchChange}
                  onKeyDown={keepEnter}
                  className="border w-full rounded-md py-2 mb-4 text-left px-2"
                />
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                  {filteredClients.map((client) => (
                    <label key={client.id} className="block mb-2">
                      <input
                        className="mr-2"
                        type="checkbox"
                        value={String(client.id)}
                        checked={tempClients.some(
                          (c) => String(c.id) === String(client.id)
                        )}
                        onChange={() => handleClientChange({ id: client.id, name: client.name })}
                      />
                      {client.name}
                    </label>
                  ))}
                </div>
                <RemoteListFooter search={clientSearch} />
              </div>
            </div>
            <div className="flex justify-end gap-4 px-8 py-4">
              <button type="button"
                onClick={discardChanges}
                className="flex-1 px-4 py-2 bg-gray-300 rounded-md"
              >
                {t('action.close')}
              </button>
              <button type="button"
                onClick={applyFilters}
                disabled={loading}
                // EV-495。bg-blue-500 は白い文字で 3.68（基準 4.5）。ほかのダイアログの主な操作のボタン
                // （AddNew の「保存」など）と同じ組にそろえた：primary/300 で 7.88、使えない間は薄く。
                className="flex-1 py-2 px-4 bg-primary/300 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('filter.title')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Filter;
